import React, { useEffect, useState } from "react";
import FirstStep from "./surveySteps/firstStep";
import SecondStep from "./surveySteps/secondStep";
import ThirdStep, { ThirdStepFormData } from "./surveySteps/thirdStep";
import { axiosRequestData } from "../../helpers/axios";
import { getSendbird } from "../../components/sendbird/sendbird";
import {
    SurveyOuter,
    SurveyCard,
    ProgressSection,
    ProgressBar,
    ProgressItem,
    ProgressItemIcon,
    ProgressItemLabel,
} from "./survey.styled";
import { SendbirdUser } from "../../components/sendbird/chat";
import { getUser } from "../../components/sendbird/sendbird.hooks";
import { pgVIPOnboardingTab, pgVipCoachingSurvey } from "../../components/mixPanelEvents/mixPanelEvents";
import FileSizeErrorModal from "../../components/fileSizeErrorModal";

export interface SurveyProps {
    onComplete: () => void;
    setUser: (user: SendbirdUser | null) => void;
}

type OnboardingStep = 1 | 2 | 3;

const getItemState = (itemStep: number, currentStep: OnboardingStep): 'active' | 'done' | 'inactive' => {
    if (itemStep < currentStep) return 'done';
    if (itemStep === currentStep) return 'active';
    return 'inactive';
};

const VipCoachingSurvey: React.FC<SurveyProps> = ({ onComplete, setUser }) => {
    const [step, setStep] = useState<OnboardingStep>(1);
    const [uploadedVideo, setUploadedVideo] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [fileSizeErrorOpen, setFileSizeErrorOpen] = useState(false);

    useEffect(() => {
        pgVIPOnboardingTab({ Page: 'Upload' });
    }, []);

    const handleVideoUpload = (file: File) => {
        setUploadedVideo(file);
        setStep(2);
        pgVIPOnboardingTab({ Page: 'Submit' });
    };

    const handleGoToStep3 = () => {
        setStep(3);
        pgVIPOnboardingTab({ Page: 'About You' });
    };

    const handleRemoveVideo = () => {
        setUploadedVideo(null);
        setStep(1);
    };

    const submitSurvey = async (formData: ThirdStepFormData) => {
        if (!uploadedVideo) return;

        setIsSubmitting(true);
        setSubmitError(null);

        try {
            pgVipCoachingSurvey({
                ballContact: formData.ballContact,
                biggestMiss: formData.biggestMiss,
            });

            // Step 1 – get Sendbird credentials and channel URL from the server
            const boardingRes = await axiosRequestData('POST', '/wp-json/pg/v1/vip/boarding', {});
            const { success, user_id, access_token, channel_url, message: errorMsg } = boardingRes.data;

            if (!success) {
                throw new Error(errorMsg || 'Boarding request failed.');
            }

            // Step 2 – connect to Sendbird and get the channel
            const sb = await getSendbird();
            await sb.connect(user_id, access_token);
            const channel = await sb.groupChannel.getChannel(channel_url);

            // Determine role and fetch current upload count
            const isCoach = user_id.startsWith('vip_coach_');
            const metaKey = isCoach ? 'coach_video_num' : 'user_video_num';
            const meta = await channel.getMetaData([metaKey]);
            const uploadNum = Number(meta?.[metaKey]) || 1;

            // Helper: send a text message and await success
            const sendText = (text: string): Promise<void> =>
                new Promise((resolve, reject) => {
                    channel
                        .sendUserMessage({ message: text })
                        .onSucceeded(() => resolve())
                        .onFailed(reject);
                });

            // Helper: send the video file with role-based name and await success
            const sendFile = (file: File): Promise<void> =>
                new Promise((resolve, reject) => {
                    const fileName = isCoach ? `Fix ${uploadNum}` : `Swing ${uploadNum}`;
                    channel
                        .sendFileMessage({
                            file,
                            fileName,
                            thumbnailSizes: [
                                { maxWidth: 200, maxHeight: 200 },
                                { maxWidth: 480, maxHeight: 480 },
                            ],
                        })
                        .onSucceeded(async () => {
                            await channel.updateMetaData({ [metaKey]: String(uploadNum + 1) }, true);
                            resolve();
                        })
                        .onFailed(reject);
                });

            // Step 3 – send messages in sequence, exactly matching boarding.js order
            await sendText(`How is your ball contact? \n${formData.ballContact}`);
            await sendText(`What is your biggest miss? \n${formData.biggestMiss}`);
            await sendText(formData.message);
            await sendFile(uploadedVideo);

            // Step 4 – mark boarding as done
            await axiosRequestData('POST', '/wp-json/pg/v1/vip/boardingdone', {});
            
            const user = await getUser();
            user.data.user_role = user.data.user_id.startsWith('vip_coach_') ? 'coach' : 'subscriber';
            setUser(user.data as SendbirdUser);
            
            // Step 5 – navigate to chat
            onComplete();
        } catch (err: any) {
            const msg =
                err?.data?.message ||
                err?.message ||
                'Something went wrong. Please try again.';
            setSubmitError(msg);
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <>
        <SurveyOuter>
            <SurveyCard>
                <ProgressSection step={step}>
                    <ProgressBar>
                        {([1, 2, 3] as const).map((n, i) => {
                            const labels = [['UPLOAD', 'SWING'], ['SUBMIT', 'SWING'], ['About', 'You']];
                            const state = getItemState(n, step);
                            return (
                                <ProgressItem key={n} state={state}>
                                    <ProgressItemIcon state={state}>{n}</ProgressItemIcon>
                                    <ProgressItemLabel>
                                        {labels[i][0]}<br />{labels[i][1]}
                                    </ProgressItemLabel>
                                </ProgressItem>
                            );
                        })}
                    </ProgressBar>
                </ProgressSection>

                {step === 1 && (
                    <FirstStep
                        onVideoUpload={handleVideoUpload}
                        onFileSizeError={() => setFileSizeErrorOpen(true)}
                    />
                )}
                {step === 2 && uploadedVideo && (
                    <SecondStep
                        video={uploadedVideo}
                        onBack={() => setStep(1)}
                        onSubmit={handleGoToStep3}
                        onRemove={handleRemoveVideo}
                    />
                )}
                {step === 3 && (
                    <ThirdStep
                        onBack={() => setStep(2)}
                        onSave={submitSurvey}
                        isLoading={isSubmitting}
                        error={submitError}
                    />
                )}
            </SurveyCard>
        </SurveyOuter>
        <FileSizeErrorModal
            isOpen={fileSizeErrorOpen}
            onClose={() => setFileSizeErrorOpen(false)}
            page="Upload"
            type="Member"
        />
        </>
    );
};

export default VipCoachingSurvey;